import { useState } from 'react';
import styled, { css } from 'styled-components';
import { FormikProvider, useFormik } from 'formik';
import router from 'next/router';
import { use100vh } from 'react-div-100vh';

import { FeatureFlags } from 'featureFlags/constants';
import { GiftCardsFooter } from 'gift-cards/components/GiftCardsFooter';
import { GiftCardsCarouselDesktop } from 'gift-cards/components/pdp/GiftCardsCarouselDesktop';
import { GiftCardsCarouselMobile } from 'gift-cards/components/pdp/GiftCardsCarouselMobile';
import { GiftCardDetails } from 'gift-cards/components/pdp/GiftCardDetails';
import { detailsModalInitialFormValues } from 'gift-cards/enums/constants';
import { useGiftCardsState } from 'gift-cards/hooks/useGiftCards';
import { IGiftCardDetailsForm } from 'gift-cards/types';
import { detailsModalValidationSchema } from 'gift-cards/utils/validations';
import { useLogInBeforeCheckout } from 'productTemplate/hooks/useLogInBeforeCheckout';
import { requestService } from 'shared/utils/services/requestService';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { allPromiseSettled } from 'shared/utils/allPromiseSettled';
import { t } from 'localization/utils/t';

export const GiftCardsPdp = ({ giftCardAmounts }: { giftCardAmounts: number[] }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { isMobile } = useWindowSize();
  const { giftCardSelection, selectedSpaceId, spaceInMobileEditMode, spaces, spaceSections } =
    useGiftCardsState();
  const { logInBeforeCheckout } = useLogInBeforeCheckout();
  const { isAuthenticated } = useStoreSelector((state) => state.auth);
  const [isFooterExpanded, setIsFooterExpanded] = useState(false);
  const windowHeight = use100vh();

  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_GUEST_CHECKOUT);

  const handleSubmit = async ({
    amount,
    customAmount,
    recipientName,
    senderName,
    recipientEmail,
    message,
  }) => {
    try {
      // create the space
      const space = await requestService({
        url: '/web-api/v1/spaces',
        method: 'post',
        data: {
          deviceWidth: 294,
          deviceHeight: 440,
          groupId: spaces[selectedSpaceId].groupId,
          background: spaces[selectedSpaceId].background,
        },
      });
      // create each item for space
      if (spaceSections[selectedSpaceId]?.subsections.length > 0) {
        const createSpaceItems = spaces[selectedSpaceId].items.map((item) => {
          return requestService({
            url: '/web-api/v1/spaces/items',
            method: 'post',
            data: {
              spaceId: space.data.id,
              placement: item.placement,
              productTemplateSpaceItemRequest: item.productTemplate.slug,
            },
          });
        });
        /* creation of items can happen async from creation of giftcard since gift card request
         only relies on the space id. No need to await this
         */
        void allPromiseSettled(createSpaceItems);
      }

      const priceCents = (amount || customAmount) * 100;

      const { giftCardId } = await requestService({
        url: '/web-api/v1/gift-cards',
        method: 'post',
        data: {
          message,
          recipientEmail,
          recipientName,
          senderName,
          spaceId: space.data.id,
        },
      });

      if (giftCardId) {
        const checkoutLink = `/checkout?giftCardId=${String(giftCardId)}&priceCents=${priceCents}`;

        sendTrackingEvent('CART_ADD', {
          gift_card_space_id: selectedSpaceId,
          purchase_gc_amount_cents: priceCents,
          currency_code: 'USD',
        });

        if (!isAuthenticated && !isGuestCheckoutEnabled) {
          logInBeforeCheckout(checkoutLink);
          return;
        }

        void router.push(checkoutLink);
      }
    } catch (error) {
      bugsnag(error, { context: 'Error creating gift card' });
    }
  };

  const formik = useFormik<IGiftCardDetailsForm>({
    initialValues: detailsModalInitialFormValues,
    onSubmit: handleSubmit,
    validationSchema: detailsModalValidationSchema(t),
  });

  const GiftCardsCarousel = isMobile ? GiftCardsCarouselMobile : GiftCardsCarouselDesktop;

  return (
    <FormikProvider value={formik}>
      <GiftCardsPdp.Wrapper>
        <GiftCardsPdp.ContentWrapper
          $isFooterExpanded={isFooterExpanded}
          $spaceInMobileEditMode={spaceInMobileEditMode}
          $height={windowHeight}
          $giftCardSelection={giftCardSelection}
        >
          {giftCardSelection ? (
            <GiftCardsCarousel
              selectedIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
              initialSlide={selectedIndex}
            />
          ) : (
            <GiftCardDetails giftCardAmounts={giftCardAmounts} darkTheme={false} />
          )}
        </GiftCardsPdp.ContentWrapper>
        <GiftCardsFooter
          detailsFormProps={formik}
          isFooterExpanded={isFooterExpanded}
          setIsFooterExpanded={setIsFooterExpanded}
        />
      </GiftCardsPdp.Wrapper>
    </FormikProvider>
  );
};

GiftCardsPdp.Wrapper = styled.div``;

GiftCardsPdp.ContentWrapper = styled.div<{
  $isFooterExpanded: boolean;
  $spaceInMobileEditMode: string;
  $height: number;
  $giftCardSelection: boolean;
}>`
  height: 100%;
  display: flex;
  justify-content: center;
  width: auto;
  transition: height 0.3s ease-in-out;

  ${({ $spaceInMobileEditMode, $isFooterExpanded, $height, $giftCardSelection }) =>
    $spaceInMobileEditMode
      ? css`
          height: calc(${$height}px - 50px);
          align-items: flex-start;
          overflow: hidden;
        `
      : css`
          align-items: flex-end;
          height: ${$giftCardSelection && `calc(${$height}px - 150px)`};

          @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
            height: ${$isFooterExpanded ? `calc(100vh - 268px);` : `calc(100vh - 153px);`};
          }
        `}
`;
